import Accordion from './Accordion'
import Button from './Button'
import GoBackButton from './GoBackButton'
import Heading from './Heading'
import LinkButton from './LinkButton'
import { scrollIntoView, scrollToTop } from '../utils/Scroll'
import { Trans, useTranslation } from 'react-i18next'
import { ArrowUpIcon24, ChevronLeftIcon24 } from '../icons'
import React, { ComponentProps } from 'react'

type FaqEntry = {
  id: number
  question: string
  answer: string
}

interface FaqProps extends ComponentProps<'div'> {
  faqLength: number
  faqSourceLocale: string
  'data-testid': string
  className?: string
}

export default function Faq({
  faqLength,
  faqSourceLocale,
  'data-testid': testId,
  className,
  ...props
}: Readonly<FaqProps>) {
  const { t } = useTranslation(faqSourceLocale)

  const faq: FaqEntry[] = Array.from(
    { length: faqLength },
    (_: number, index: number): FaqEntry => ({
      id: index,
      question: t(`faq.question_${index}`),
      answer: t(`faq.answer_${index}`)
    })
  )

  function onQuestionClick(entry: FaqEntry) {
    const id = `faq-${entry.id}`
    scrollIntoView(id)

    const accordion = document.querySelector(`#${id}`)

    // ensure that accordion exists and is not open
    if (accordion && !('open' in accordion.attributes)) {
      // open accordion
      accordion.querySelector('summary')?.click()
    }
  }

  return (
    <div className={className} {...props} data-testid={`${testId}-div`}>
      <div
        className="flex items-center gap-0.5"
        data-testid={`${testId}-title-wrapper`}
      >
        <GoBackButton data-testid={`${testId}-go-back-button`} />
        <Heading tag="h1">{t('faq.title')}</Heading>
      </div>
      {/* Questions */}
      <ol
        className="flex flex-col gap-0.5 pb-2"
        data-testid={`${testId}-question-wrapper`}
      >
        {faq.map((qa) => (
          <li key={qa.id} className="px-1">
            <LinkButton
              onClick={() => onQuestionClick(qa)}
              className="inline-flex pl-[2px]"
            >
              <ChevronLeftIcon24
                data-testid={`${testId}-questions-${qa.id}-link-icon`}
              />
              <span
                className="inline-flex ml-0.5"
                data-testid={`${testId}-question-${qa.id}`}
              >
                {qa.question}
              </span>
            </LinkButton>
          </li>
        ))}
      </ol>
      {/* Accordions */}
      <div className="text-left mr-auto bg-white dark:bg-cool-gray-700 w-full">
        <hr />
        {faq.map((qa) => (
          <Accordion
            id={`faq-${qa.id}`}
            key={qa.id}
            summary={qa.question}
            data-testid={`${testId}-accordion-${qa.id}`}
          >
            <p data-testid={`${testId}-answer-${qa.id}`}>
              <Trans
                i18nKey={'faq.answer'}
                components={{
                  emailBold: (
                    <span
                      data-testid={`${testId}-answer-${qa.id}-email`}
                      className="font-bold"
                    />
                  ),
                  internBold: (
                    <span
                      data-testid={`${testId}-answer-${qa.id}-intern`}
                      className="font-bold"
                    />
                  ),
                  externBold: (
                    <span
                      data-testid={`${testId}-answer-${qa.id}-extern`}
                      className="font-bold"
                    />
                  ),
                  supportMailto: (
                    <a
                      data-testid={`${testId}-answer-${qa.id}-mailto`}
                      href="mailto:InfraGO-IT-Support@deutschebahn.com"
                    />
                  )
                }}
              >
                {qa.answer}
              </Trans>
            </p>
          </Accordion>
        ))}
      </div>
      <div className="flex mt-1 flex-row-reverse">
        <Button
          variant="tertiary-plain"
          onClick={scrollToTop}
          data-testid={`${testId}-back-to-top-button`}
        >
          <ArrowUpIcon24 data-testid={`${testId}-back-to-top-button-icon`} />
          <span
            className="pl-0.5"
            data-testid={`${testId}-back-to-top-button-text`}
          >
            {t('faq.back_to_top_button')}
          </span>
        </Button>
      </div>
    </div>
  )
}
